// Libs
import { useState } from 'react';

// RDS
import Button from '@ramsey-design-system/button';
import Heading from '@ramsey-design-system/heading';
import { MinusIcon, PlusIcon } from '@ramsey-design-system/icons';
import Link from '@ramsey-design-system/link';

// Assets
import lessonsData, { Lesson } from '~fpu/shared/config/fpu-lesson-config';

// Components
import {
  LessonVideoModal,
  LessonVideoModalTrigger
} from '~fpu/modules/FpuDashboard/components/Lessons/LessonVideoModal';

// Utils
import { ZENDESK_BRAND_PARAM } from '~fpu/config/constants';
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import { PageAnalyticsWrapper } from '~fpu/shared/utils/PageAnalyticsWrapper';

interface VideoThumbnailProps {
  thumb: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

export function VideoThumbnail({
  thumb,
  title,
  subtitle,
  className
}: VideoThumbnailProps) {
  return (
    <button
      onClick={() =>
        analyticsLogger.trackElementClicked({
          element: 'Lesson recap',
          label: `${title}`
        })
      }
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          analyticsLogger.trackElementClicked({
            element: 'Lesson recap',
            label: `${title}`
          });
        }
      }}
    >
      <img
        loading="lazy"
        src={thumb}
        alt={title}
        data-testid="[Promos] video download"
        className={`mb-0.5 ${className}`}
      />

      {subtitle ? (
        <Heading color="alternate" level="1" className="text-3 font-semibold">
          {subtitle}
        </Heading>
      ) : null}

      {title ? (
        <Heading color="brand" level="2" className="text-3 font-normal">
          {title}
        </Heading>
      ) : null}

      <p className="text-2 text-gray-50 uppercase">video</p>
    </button>
  );
}

function VirtualClassResources() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTrigger = (lesson: Lesson, playerId: number) => {
    setSelectedLesson(lesson);
    setSelectedPlayerId(playerId);
    setIsModalOpen(true);
  };

  const handleTitleClick = (index: number, element: string, label: string) => {
    analyticsLogger.trackElementClicked({ element: element, label: label });
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  return (
    <PageAnalyticsWrapper category="Coordinator" page="Virtual Class Resources">
      <section className="w-full rounded bg-white p-2 sm:p-3 xl:p-4">
        <div>
          <Button
            className="text-5 text-blue-60 justify-between px-0 text-left font-bold uppercase"
            appearance="subtle"
            onClick={() => handleTitleClick(0, 'Lesson Links', 'Header')}
            icon={activeIndex === 0 ? MinusIcon : PlusIcon}
            iconPosition="right"
          >
            Lesson Recaps (4th Edition)
          </Button>

          {activeIndex === 0 && (
            <ul className="gap-lg grid grid-cols-1 py-3 sm:grid-cols-2 md:grid-cols-3">
              {lessonsData.map((lesson, index) => (
                <li
                  key={index}
                  className="border-gray-30 rounded-sm border p-1"
                >
                  <LessonVideoModalTrigger
                    key={`lesson-video-trigger-${index}`}
                    lesson={lesson}
                    playerId={index}
                    onTrigger={handleTrigger}
                  />
                </li>
              ))}
            </ul>
          )}

          <LessonVideoModal
            lesson={selectedLesson}
            playerId={selectedPlayerId}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
          />
        </div>

        <div>
          <Button
            className="text-5 text-blue-60 justify-between px-0 text-left font-bold uppercase"
            appearance="subtle"
            onClick={() => handleTitleClick(1, 'Downloads', 'Header')}
            icon={activeIndex === 1 ? MinusIcon : PlusIcon}
            iconPosition="right"
          >
            Downloads
          </Button>

          {activeIndex === 1 && (
            <div className="px-6 py-3">
              <Link
                className="text-6 mb-2"
                target="_blank"
                rel="noopener noreferrer"
                href="https://cdn.ramseysolutions.net/church/coordinator-tools/download/resources/virtual-fpu-coordinator-overview-v3.pdf"
                onClick={() =>
                  analyticsLogger.trackElementClicked({
                    element: 'Downloads',
                    label: 'Virtual Class Checklist PDF'
                  })
                }
              >
                Virtual Class Checklist PDF
              </Link>

              <p className="mb-2">
                This step-by-step guide will walk you through everything you
                need to do to prep for and lead your virtual class.
              </p>

              <Link
                className="text-6"
                target="_blank"
                rel="noopener noreferrer"
                href="https://cdn.ramseysolutions.net/church/coordinator-tools/download/coordinator-guide/4th-edition/07-2024/fpu-coordinator-guide-4th-edition.pdf"
                onClick={() =>
                  analyticsLogger.trackElementClicked({
                    element: 'Downloads',
                    label: 'Complete Coordinator Guide'
                  })
                }
              >
                Complete Coordinator Guide (4th Edition)
              </Link>
            </div>
          )}
        </div>

        <div>
          <Button
            className="text-5 text-blue-60 justify-between px-0 text-left font-bold uppercase"
            appearance="subtle"
            onClick={() => handleTitleClick(2, 'Training', 'Header')}
            icon={activeIndex === 2 ? MinusIcon : PlusIcon}
            iconPosition="right"
          >
            Training
          </Button>

          {activeIndex === 2 && (
            <div className="px-6 py-3">
              <p className="mb-2">
                This 23-minute video lesson series will teach you how to lead a
                class with confidence.
              </p>

              <Button
                element="a"
                appearance="primary"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.ramseysolutions.com/ramseyplus/coordinator/training"
                onClick={() =>
                  analyticsLogger.trackElementClicked({
                    element: 'Training',
                    label: 'Watch Videos'
                  })
                }
              >
                Watch Videos
              </Button>
            </div>
          )}
        </div>

        <div>
          <Button
            className="text-5 text-blue-60 justify-between px-0 text-left font-bold uppercase"
            appearance="subtle"
            onClick={() => handleTitleClick(3, 'Questions', 'Header')}
            icon={activeIndex === 3 ? MinusIcon : PlusIcon}
            iconPosition="right"
          >
            Questions?
          </Button>

          {activeIndex === 3 && (
            <div className="px-6 py-3">
              <p className="mb-2">
                Visit our help center to find answers to frequently-asked
                questions.
              </p>

              <Button
                element="a"
                appearance="primary"
                target="_blank"
                rel="noopener noreferrer"
                href={`https://financialpeace.help.ramseysolutions.com/hc/en-us/sections/360007633251-Coordinate-a-Class${ZENDESK_BRAND_PARAM}`}
                onClick={() =>
                  analyticsLogger.trackElementClicked({
                    element: 'Questions',
                    label: 'Go to Help Center'
                  })
                }
              >
                Go to Help Center
              </Button>
            </div>
          )}
        </div>
      </section>
    </PageAnalyticsWrapper>
  );
}

export default VirtualClassResources;
export { VirtualClassResources };
